import { useSelect } from 'downshift';
import _ from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { MultiSelectItem } from '../../commonTypes/multiSelect.types';
import { Icon } from '../Icon/icon';
import classNames from 'classnames';
import { Tooltip } from '../Tooltip/Tooltip';
import { stringWithLineBreak } from '../../utils/helpers';

export interface IMultiSelectComboboxProps {
  items: MultiSelectItem[];
  textBoxDefaultText?: string;
  textBoxLabelText?: string;
  showDropdownIntroText?: boolean;
  dropdownIntroText?: string;
  labelTooltip?: string;
  labelClass?: string;
  showRemoveSelectionsButton?: boolean;
  enableShowAllOption?: boolean;
  showAllOptionText?: string;
  removeSelectionsText?: string;
  useHeadingInsteadOfLabel?: boolean;
  headingLevel?: number;
  /** Disables enableShowAllOption if set*/
  maxSelectedItems?: number;
  onSelectedItemsChange?: (selectedItems: MultiSelectItem[]) => void;
}

const SHOW_ALL_KEY: string = 'showall';

//Default props
MultiSelectCombobox.defaultProps = {
  textBoxDefaultText: '0 valda',
  textBoxLabelText: 'Välj regioner',
  showDropdownIntroText: false,
  dropdownIntroText: 'Regioner A till Ö',
  labelClass: '',
  showRemoveSelectionsButton: true,
  removeSelectionsText: 'Rensa val',
  enableShowAllOption: true,
  showAllOptionText: 'Visa alla',
  items: [],
  useHeadingInsteadOfLabel: false,
  headingLevel: 2,
  onSelectedItemsChange: (selectedItems) => {
    console.log(selectedItems);
  },
} as Partial<IMultiSelectComboboxProps>;

export function MultiSelectCombobox(props: IMultiSelectComboboxProps) {
  const firstUpdate = useRef(true);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  // Determine if the show all option should be enabled based on maxSelectedItems
  const shouldEnableShowAllOption = props.enableShowAllOption && !props.maxSelectedItems;
  const [internalItems, setInternalItems] = useState(
    shouldEnableShowAllOption
      ? [
          {
            key: SHOW_ALL_KEY,
            value: props.showAllOptionText,
          } as MultiSelectItem,
          ...props.items,
        ]
      : [...props.items]
  );
  useEffect(() => {
    const incomingList = shouldEnableShowAllOption
      ? [
          {
            key: SHOW_ALL_KEY,
            value: props.showAllOptionText,
          } as MultiSelectItem,
          ...props.items,
        ]
      : [...props.items];

    if (!_.isEqual(internalItems, incomingList)) {
      setInternalItems(incomingList);
    }
  }, [props.items, shouldEnableShowAllOption]);

  const [selectedItems, setSelectedItems] = useState<MultiSelectItem[]>(
    internalItems.filter((x) => x.isSelectedByDefault)
  );
  useEffect(() => setSelectedItems(internalItems.filter((x) => x.isSelectedByDefault)), [internalItems]);

  //callback for selectedItems
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (props.onSelectedItemsChange) {
      props.onSelectedItemsChange(selectedItems.filter((x) => x.key !== SHOW_ALL_KEY).slice());
    }
  }, [selectedItems]);

  const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
    items: internalItems,
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;
      const index = selectedItems.indexOf(selectedItem);

      //handle select all
      if (shouldEnableShowAllOption && selectedItem.key === SHOW_ALL_KEY) {
        setSelectedItems(index === 0 ? [] : [...internalItems.slice()]);
      } else {
        if (index > -1) {
          setSelectedItems([...selectedItems.slice(0, index), ...selectedItems.slice(index + 1)]);
        } else {
          // Check if adding this item would exceed maxSelectedItems
          if (props.maxSelectedItems && selectedItems.length >= props.maxSelectedItems) {
            // If already at max, don't add the new item
            return;
          }
          setSelectedItems([...selectedItems, selectedItem]);
        }
      }
    },
    onIsOpenChange: ({ isOpen, type }) => {
      // Return focus to toggle button when closing with Escape
      if (type === useSelect.stateChangeTypes.MenuKeyDownEscape && !isOpen) {
        setTimeout(() => {
          if (toggleButtonRef.current) {
            toggleButtonRef.current.focus();
          }
        }, 0);
      }
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
          };
        default:
          return changes;
      }
    },
  });

  const getSelectedText = (): string => {
    if (selectedItems.length === 0) {
      return `${props.textBoxDefaultText}`;
    }

    if (selectedItems.length === 1) {
      return `${selectedItems[0].value}`;
    }

    return `${selectedItems.length} valda`;
  };

  const handleDeselectAll = (e) => {
    e.preventDefault();
    setSelectedItems([]);
  };

  const HTag = `h${props.headingLevel}`;

  return (
    <div className="cmb-wrapper">
      <div className="cmb-wrapper__inner">
        {props.useHeadingInsteadOfLabel && (
          <HTag {...getLabelProps()} className={`${props.labelClass} inline`}>
            {props.textBoxLabelText}
          </HTag>
        )}
        {!props.useHeadingInsteadOfLabel && (
          <label {...getLabelProps()} className={props.labelClass}>
            {props.textBoxLabelText}
          </label>
        )}
        {props.labelTooltip && (
          <Tooltip text={stringWithLineBreak(props.labelTooltip)} enableEpiserverOnPageEditing={true} />
        )}
        <div className="cmb-wrapper__tools">
          <button type="button" className="cmb-wrapper__inner__btn" {...getToggleButtonProps({ ref: toggleButtonRef })}>
            {getSelectedText()}
            <Icon
              className={`cmb-wrapper__inner__btn__arrow ${isOpen ? 'cmb-wrapper__inner__btn__arrow--expanded' : ''}`}
              iconType="fontawesome/chevron-left"
            />
          </button>
          {props.showRemoveSelectionsButton && (
            <button className="cmb-wrapper__inner__clean" type="button" onClick={handleDeselectAll}>
              {props.removeSelectionsText}
            </button>
          )}
        </div>
        <ul
          className={`cmb-wrapper__inner__expandable ${isOpen ? 'cmb-wrapper__inner__expandable--expanded' : ''}`}
          {...getMenuProps()}
          role="listbox"
          style={{ display: isOpen ? 'block' : 'none' }}>
          {props.showDropdownIntroText && props.dropdownIntroText && (
            <li className="presentation-text" role="presentation" aria-hidden="false">
              {props.dropdownIntroText}
            </li>
          )}
          {internalItems.map((item, index) => {
            // Determine if the item should be disabled
            const isDisabled =
              props.maxSelectedItems &&
              selectedItems.length >= props.maxSelectedItems &&
              !selectedItems.some((x) => x.key === item.key);

            return (
              <li
                className={classNames({
                  'cmb-wrapper__active': highlightedIndex === index,
                  'cmb-wrapper__disabled': isDisabled,
                })}
                key={`${item.key}_${index}`}
                {...getItemProps({ item, index, disabled: isDisabled })}
                aria-selected={selectedItems.some((x) => x.key === item.key)}
                role="option"
                aria-label={`${item.value} ${
                  selectedItems.some((x) => x.key === item.key) ? 'Valt' : isDisabled ? 'Otillgängligt' : 'Ej valt'
                }`}>
                <input
                  type="checkbox"
                  checked={selectedItems.some((x) => x.key === item.key)}
                  value={item.key}
                  disabled={isDisabled}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <span />
                {item.value}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
